import { ImeiPicker, Scanner } from '../components/UnitsDialog';
import DocShare from '../components/DocShare';
import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import { useSearchParams } from 'react-router-dom';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Download, Eye, FileText, Truck, CheckCircle2, Trash2, CreditCard, Share2, ArrowRightLeft, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { salesProFilename, downloadBlob, shareWhatsapp } from '../lib/salespro';
import DocumentActions from '../components/ui/document-actions';

const STATUS_OPTIONS = [
  { value: 'presupuesto', label: 'Presupuesto', color: 'bg-purple-100 text-purple-800' },
  { value: 'abierto', label: 'Abierto', color: 'bg-gray-100 text-gray-800' },
  { value: 'pendiente_procesar', label: 'Pendiente procesar', color: 'bg-orange-100 text-orange-800' },
  { value: 'procesada', label: 'Procesada', color: 'bg-blue-100 text-blue-800' },
  { value: 'pendiente_despacho', label: 'Pendiente despacho', color: 'bg-orange-100 text-orange-800' },
  { value: 'en_transito', label: 'En tránsito', color: 'bg-purple-100 text-purple-800' },
  { value: 'despachado', label: 'Despachado', color: 'bg-green-100 text-green-800' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' }
];

const statusInfo = (s) => STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];

const FinancingPlan = ({ sale, setSale, total }) => {
  const down = Math.min(parseFloat(sale.down_payment) || 0, total);
  const n = Math.max(1, parseInt(sale.installments) || 1);
  const each = (total - down) / n;
  const step = sale.frequency === 'custom' ? (parseInt(sale.custom_days) || 0) : sale.frequency === 'mensual' ? 30 : parseInt(sale.frequency);
  const dates = Array.from({ length: Math.min(n, 12) }, (_, i) => {
    const d = new Date();
    if (sale.frequency === 'mensual') d.setMonth(d.getMonth() + i + 1); else d.setDate(d.getDate() + step * (i + 1));
    return d.toLocaleDateString('es-VE');
  });
  return (
    <div className="rounded-lg border border-primary/30 bg-[#F0FBF7] p-3 space-y-2" data-testid="financing-plan">
      <p className="text-sm font-bold text-[#022C22]">Plan de financiamiento</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><Label className="text-xs">Inicial ($)</Label><Input type="number" value={sale.down_payment} onChange={(e) => setSale({ ...sale, down_payment: e.target.value })} data-testid="financing-down-payment" /></div>
        <div><Label className="text-xs">Frecuencia</Label>
          <select value={sale.frequency} onChange={(e) => setSale({ ...sale, frequency: e.target.value })} className="w-full px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" data-testid="financing-frequency">
            <option value="7">Cada 7 días</option><option value="14">Cada 14 días</option><option value="mensual">Mensual</option><option value="custom">Personalizada</option>
          </select></div>
        <div><Label className="text-xs">N° de cuotas</Label><Input type="number" min="1" value={sale.installments} onChange={(e) => setSale({ ...sale, installments: e.target.value })} data-testid="financing-installments" /></div>
        {sale.frequency === 'custom' && <div><Label className="text-xs">Cada (días)</Label><Input type="number" min="1" value={sale.custom_days} onChange={(e) => setSale({ ...sale, custom_days: e.target.value })} data-testid="financing-custom-days" /></div>}
      </div>
      <p className="text-xs text-[#022C22]" data-testid="financing-summary">Financiado: <b>${(total - down).toFixed(2)}</b> en {n} cuota(s) de <b>${each.toFixed(2)}</b></p>
      <p className="text-[11px] text-[#64748B]">Vencimientos: {dates.join(' · ')}{n > 12 ? ' …' : ''}</p>
      <p className="text-[11px] text-[#64748B]">Requiere línea de crédito aprobada. Se notificará al vendedor, admin, superadmin y al cliente.</p>
    </div>
  );
};

export default function Sales() {
  const { user } = useAuth();
  const { license } = useLicense();
  const vFlags = license?.vertical_flags || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailSale, setDetailSale] = useState(null);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterCompany, setFilterCompany] = useState(searchParams.get('company') || '');
  const [productSearch, setProductSearch] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [filterDoc, setFilterDoc] = useState(searchParams.get('doc') || '');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [salePayments, setSalePayments] = useState([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method_id: '', method_name: '', reference: '', notes: '' });

  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const isAdmin = user?.role_id === 'admin';
  const isVendedor = user?.role_id === 'vendedor';
  const canProcess = isMaster || isAdmin;

  const [newSale, setNewSale] = useState({
    company_id: '',
    client_id: '',
    items: [],
    document_type: 'factura',
    operation_type: 'contado',
    price_list: 'A',
    tax_rate: 16,
    notes: '',
    down_payment: '',
    frequency: '7',
    installments: 4,
    custom_days: 10
  });

  const loadAll = async () => {
    try {
      const cid = getCurrentCompanyId();
      const [salesRes, clientsRes, prodsRes, compsRes, methodsRes] = await Promise.all([
        api.getSales({ company_id: cid || undefined }),
        api.getClients({ company_id: cid || undefined }),
        api.getProducts({ company_id: cid || undefined }),
        api.getCompanies(),
        api.getPaymentMethods().catch(() => ({ data: [] }))
      ]);
      setSales(salesRes.data);
      setClients(clientsRes.data);
      setProducts(prodsRes.data);
      setCompanies(compsRes.data);
      setPaymentMethods(methodsRes.data || []);
      if (compsRes.data.length > 0) {
        setNewSale(n => ({ ...n, company_id: cid || user?.company_id || compsRes.data[0].company_id }));
      }
    } catch (e) {
      toast.error('SalesPRO: Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  };

  const loadSalePayments = async (saleId) => {
    try {
      const res = await api.getSalePayments(saleId);
      setSalePayments(res.data || []);
    } catch {
      setSalePayments([]);
    }
  };

  const openPaymentDialog = (sale) => {
    setPaymentForm({ amount: (sale.total - (sale.amount_paid || 0)).toFixed(2), method_id: paymentMethods[0]?.method_id || '', method_name: paymentMethods[0]?.name || '', reference: '', notes: '' });
    setPaymentOpen(true);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { (async () => { await loadAll(); })(); }, []);
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreateOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const submitPayment = async () => {
    if (!detailSale) return;
    const amt = parseFloat(paymentForm.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('SalesPRO: Monto inválido'); return; }
    try {
      await api.registerPaymentDetailed(detailSale.sale_id, {
        amount: amt,
        method_id: paymentForm.method_id || null,
        method_name: paymentForm.method_name || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      });
      toast.success('SalesPRO: Pago registrado');
      setPaymentOpen(false);
      loadAll();
      // Refrescar detalle
      const updated = await api.getSales();
      const s = updated.data.find(x => x.sale_id === detailSale.sale_id);
      if (s) { setDetailSale(s); loadSalePayments(s.sale_id); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al registrar pago');
    }
  };

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || id?.substring(0, 8) || '-';
  const productName = (id) => products.find(p => p.product_id === id)?.name || '-';

  const filteredSales = sales.filter(s => {
    if (filterStatus && s.status !== filterStatus) return false;
    if (filterCompany && s.company_id !== filterCompany) return false;
    if (filterDoc && (s.document_type || 'factura') !== filterDoc) return false;
    return true;
  });

  const totalAmount = filteredSales.reduce((sum, s) => sum + (s.total || 0), 0);
  const totalCount = filteredSales.length;

  const productResults = (() => {
    const q = productSearch.trim().toLowerCase();
    const list = products.filter(p => !newSale.company_id || p.company_id === newSale.company_id);
    if (!q) return list.slice(0, 30);
    return list.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.code || '').toLowerCase().includes(q) ||
      (p.model || '').toLowerCase().includes(q) ||
      (p.marca || '').toLowerCase().includes(q)
    ).slice(0, 30);
  })();

  const updateItem = (idx, patch) => {
    const items = [...newSale.items];
    items[idx] = { ...items[idx], ...patch };
    if (patch.product_id) {
      const p = products.find(pp => pp.product_id === patch.product_id);
      if (p) {
        items[idx].name = p.name;
        items[idx].price = (newSale.price_list === 'B' && p.price_b != null) ? p.price_b : p.price;
      }
    }
    setNewSale({ ...newSale, items });
  };

  const applyPriceList = (list) => {
    const items = newSale.items.map(it => {
      const p = products.find(pp => pp.product_id === it.product_id);
      if (p) return { ...it, price: (list === 'B' && p.price_b != null) ? p.price_b : p.price };
      return it;
    });
    setNewSale({ ...newSale, price_list: list, items });
  };

  const [xfer, setXfer] = useState(null);
  const [preview, setPreview] = useState(false);
  const isSuper = ['master', 'superadmin'].includes(user?.role_id);
  const setItemPrice = (idx, val) => setNewSale((n) => { const items = [...n.items]; items[idx] = { ...items[idx], price: val }; return { ...n, items }; });
  const openXfer = async (p) => {
    try {
      const r = await api.getAvailability({ product_id: p.product_id, exclude_company: newSale.company_id });
      if (!r.data.length) { toast.error('SalesPRO: Ninguna otra sucursal tiene este modelo con stock'); return; }
      setXfer({ product: p, options: r.data });
    } catch { toast.error('SalesPRO: No se pudo consultar la disponibilidad'); }
  };
  const requestXfer = async (opt) => {
    try {
      await api.createTransferRequest({ from_company_id: opt.company_id, to_company_id: newSale.company_id, items: [{ product_id: opt.product_id, quantity: 1 }] });
      toast.success(`SalesPRO: Solicitud enviada a ${opt.company_name}. La entrega queda EN ESPERA hasta su aprobación.`);
      setXfer(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: No se pudo solicitar el traspaso'); }
  };

  const addProduct = (p) => {
    setNewSale(n => {
      const idx = n.items.findIndex(it => it.product_id === p.product_id);
      if (idx >= 0) {
        const items = [...n.items];
        items[idx] = { ...items[idx], quantity: (items[idx].quantity || 0) + 1 };
        return { ...n, items };
      }
      const price = (n.price_list === 'B' && p.price_b != null) ? p.price_b : p.price;
      return { ...n, items: [...n.items, { product_id: p.product_id, name: p.name, quantity: 1, price }] };
    });
  };

  // Escaneo con cámara: agrega el artículo leído por código de barras o IMEI; si es equipo, preselecciona ese IMEI
  const addScannedProduct = (p, imei) => {
    setNewSale(n => {
      const idx = n.items.findIndex(it => it.product_id === p.product_id);
      const price = (n.price_list === 'B' && p.price_b != null) ? p.price_b : p.price;
      let items;
      if (idx >= 0) {
        items = [...n.items];
        const cur = items[idx];
        const imeis = imei ? Array.from(new Set([...(cur.imeis || []), imei])) : (cur.imeis || []);
        items[idx] = { ...cur, quantity: imei ? Math.max(cur.quantity || 1, imeis.length) : (cur.quantity || 0) + 1, imeis };
      } else {
        items = [...n.items, { product_id: p.product_id, name: p.name, quantity: 1, price, imeis: imei ? [imei] : [] }];
      }
      return { ...n, items };
    });
  };

  const handleScan = async (code) => {
    try {
      const r = await api.scanProduct(code, newSale.company_id);
      addScannedProduct(r.data.product, r.data.imei);
      toast.success(`Agregado: ${r.data.product.name}${r.data.imei ? ` · IMEI ${r.data.imei}` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Código no encontrado en esta tienda');
    }
  };

  const removeItem = (idx) => {
    setNewSale({ ...newSale, items: newSale.items.filter((_, i) => i !== idx) });
  };

  const calcSubtotal = () => newSale.items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.quantity) || 0), 0);
  const calcTax = () => calcSubtotal() * (parseFloat(newSale.tax_rate) || 0) / 100;
  const calcTotal = () => calcSubtotal() + calcTax();

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSale.client_id) {
      toast.error('SalesPRO: Selecciona un cliente');
      return;
    }
    if (newSale.items.length === 0) {
      toast.error('SalesPRO: Agrega al menos un producto');
      return;
    }
    try {
      const isBudget = newSale.document_type === 'presupuesto';
      const isNota = newSale.document_type === 'nota_entrega';
      // Nota de entrega = siempre a crédito; presupuesto = sin pago; factura = según selector
      const op = isNota ? 'credito' : (newSale.operation_type || 'contado');
      let amount_paid = 0;
      let payment_status = 'pendiente';
      if (!isBudget && op === 'contado') {
        amount_paid = calcTotal();
        payment_status = 'pagado';
      }
      const financing = (!isBudget && op === 'credito')
        ? { frequency: newSale.frequency, installments: parseInt(newSale.installments) || 1, custom_days: parseInt(newSale.custom_days) || 0 }
        : null;
      if (financing) {
        amount_paid = Math.min(parseFloat(newSale.down_payment) || 0, calcTotal());
        payment_status = amount_paid > 0 ? 'parcial' : 'pendiente';
      }
      await api.createSale({
        client_id: newSale.client_id,
        items: newSale.items,
        total: calcTotal(),
        document_type: newSale.document_type,
        tax_rate: parseFloat(newSale.tax_rate) || 0,
        status: 'abierto',
        amount_paid,
        payment_status,
        notes: newSale.notes,
        financing,
        company_id: (isMaster || isAdmin) ? newSale.company_id : undefined
      });
      const docLabel = newSale.document_type === 'nota_entrega' ? 'Nota de entrega' : newSale.document_type === 'presupuesto' ? 'Presupuesto' : 'Factura';
      toast.success(`SalesPRO: ${docLabel} creada`);
      setCreateOpen(false);
      setNewSale({ company_id: newSale.company_id, client_id: '', items: [], document_type: 'factura', operation_type: 'contado', price_list: 'A', tax_rate: 16, notes: '', down_payment: '', frequency: '7', installments: 4, custom_days: 10 });
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'SalesPRO: Error al crear venta');
    }
  };

  const changeStatus = async (sale, newStatus) => {
    try {
      if (newStatus === 'procesada') {
        await api.processSale(sale.sale_id);
      } else {
        await api.updateDispatchInfo(sale.sale_id, { status: newStatus });
      }
      toast.success(`SalesPRO: Estado actualizado a "${statusInfo(newStatus).label}"`);
      loadAll();
      setDetailSale(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al actualizar estado');
    }
  };

  const convertBudget = async (sale, targetType) => {
    try {
      const res = await api.convertBudget(sale.sale_id, targetType);
      const label = targetType === 'nota_entrega' ? 'Nota de entrega' : 'Factura';
      toast.success(`SalesPRO: Presupuesto convertido a ${label} (${res.data.invoice_number})`);
      await loadAll();
      const updated = await api.getSales();
      const s = updated.data.find(x => x.sale_id === sale.sale_id);
      setDetailSale(s || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al convertir presupuesto');
    }
  };

  const downloadPdf = async (sale, kind) => {
    try {
      const res = kind === 'invoice' ? await api.getSaleTicket(sale.sale_id) : await api.getDispatchTicket(sale.sale_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('SalesPRO: Error al descargar PDF');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.exportSalesReport();
      downloadBlob(new Blob([res.data]), salesProFilename('Ventas', 'xlsx'));
      toast.success('SalesPRO: Reporte exportado');
    } catch (e) {
      toast.error('SalesPRO: Error al exportar');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Ventas</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestiona facturas y notas de entrega</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-sales-button">
            <Download size={16} className="mr-1" /> Excel
          </Button>
          {!user?.role_id?.includes('despacho') && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-sale-button">
                  <Plus size={16} className="mr-1" /> Nueva Venta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-sale-dialog">
                <DialogHeader>
                  <DialogTitle>Nueva Venta</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-3">
                  {(isMaster || isAdmin) && companies.length > 0 && (
                    <div>
                      <Label>Empresa</Label>
                      <select
                        value={newSale.company_id}
                        onChange={(e) => setNewSale({ ...newSale, company_id: e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                        data-testid="sale-company-select"
                        required
                      >
                        {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div data-testid="sale-client-block">
                    <Label>Cliente</Label>
                    {(() => {
                      const clientList = clients.filter(c => !newSale.company_id || c.company_id === newSale.company_id);
                      const selected = clientList.find(c => c.client_id === newSale.client_id);
                      if (selected) {
                        return (
                          <div className="flex items-center justify-between gap-2 px-3 py-2 border border-primary/40 bg-[#F0FBF7] rounded-md" data-testid="sale-client-selected">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#022C22] truncate">{selected.name}</p>
                              <p className="text-[11px] text-[#64748B]">{selected.cedula_rif || 'Sin RIF'}</p>
                            </div>
                            <Button type="button" size="sm" variant="ghost" onClick={() => { setNewSale({ ...newSale, client_id: '' }); setClientSearch(''); }} data-testid="sale-client-change">Cambiar</Button>
                          </div>
                        );
                      }
                      const q = clientSearch.trim().toLowerCase();
                      const results = (q ? clientList.filter(c => (c.name || '').toLowerCase().includes(q) || (c.cedula_rif || '').toLowerCase().includes(q)) : clientList).slice(0, 30);
                      return (
                        <div>
                          <input
                            type="text"
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            placeholder="Buscar cliente por nombre o RIF..."
                            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            data-testid="sale-client-search"
                          />
                          <div className="mt-1 border border-[#E2E8F0] rounded-md max-h-40 overflow-y-auto divide-y divide-[#F1F5F9]" data-testid="sale-client-results">
                            {results.length === 0 && <p className="text-sm text-center text-[#94A3B8] py-3">Sin clientes</p>}
                            {results.map(c => (
                              <button type="button" key={c.client_id} onClick={() => { setNewSale({ ...newSale, client_id: c.client_id }); setClientSearch(''); }}
                                className="w-full text-left px-3 py-2 hover:bg-[#F0FBF7] transition-colors" data-testid={`client-option-${c.client_id}`}>
                                <p className="text-sm font-medium text-[#022C22] truncate">{c.name}</p>
                                <p className="text-[11px] text-[#64748B]">{c.cedula_rif || 'Sin RIF'}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Productos</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center rounded-md border border-[#E2E8F0] overflow-hidden text-xs" data-testid="price-list-selector">
                          <button type="button" onClick={() => applyPriceList('A')}
                            className={`px-2.5 py-1 ${newSale.price_list === 'A' ? 'bg-primary text-primary-foreground' : 'bg-white text-[#64748B]'}`}>Precio A</button>
                          <button type="button" onClick={() => applyPriceList('B')}
                            className={`px-2.5 py-1 ${newSale.price_list === 'B' ? 'bg-primary text-primary-foreground' : 'bg-white text-[#64748B]'}`}>Precio B</button>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Buscar producto por nombre, código, modelo o marca..."
                        className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="sales-product-search"
                      />
                      <Button type="button" variant={scanOpen ? 'default' : 'outline'} onClick={() => setScanOpen(s => !s)} data-testid="sales-scan-btn">
                        <Camera size={16} className="mr-1" /> {scanOpen ? 'Cerrar' : 'Escanear'}
                      </Button>
                    </div>
                    {scanOpen && (
                      <div className="mb-3 p-2 border border-[#E2E8F0] rounded-md bg-[#F8FAFC]" data-testid="sales-scanner-panel">
                        <p className="text-[11px] text-[#64748B] mb-1">Apunta la cámara al código de barras del artículo o al IMEI del equipo. También puedes escribirlo en el buscador.</p>
                        <Scanner scanId="sales-barcode-scanner" onCode={(c) => handleScan(c)} onClose={() => setScanOpen(false)} />
                      </div>
                    )}
                    {productSearch.trim() && (
                      <div className="mb-3 border border-[#E2E8F0] rounded-md max-h-56 overflow-y-auto divide-y divide-[#F1F5F9]" data-testid="product-search-results">
                        {productResults.length === 0 && (
                          <p className="text-sm text-center text-[#94A3B8] py-4">Sin resultados para «{productSearch}»</p>
                        )}
                        {productResults.map(p => {
                          const inCart = newSale.items.find(it => it.product_id === p.product_id);
                          const priceShown = (newSale.price_list === 'B' && p.price_b != null) ? p.price_b : p.price;
                          const isService = p.item_type === 'servicio';
                          const noStock = !isService && (p.stock || 0) <= 0;
                          return (
                            <button
                              type="button"
                              key={p.product_id}
                              onClick={() => (noStock ? openXfer(p) : addProduct(p))}
                              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F0FBF7] transition-colors"
                              data-testid={`product-option-${p.product_id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {p.code && <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{p.code}</span>}
                                  <span className="text-sm font-medium text-[#022C22] truncate">{p.name}</span>
                                </div>
                                <p className={`text-[11px] mt-0.5 ${noStock ? 'text-red-600 font-semibold' : 'text-[#64748B]'}`}>
                                  {isService ? 'Servicio' : (noStock ? '⚠ Sin stock · toca para buscar en otras tiendas' : `Stock: ${p.stock}`)}{p.marca ? ` · ${p.marca}` : ''}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-bold text-primary">${(priceShown || 0).toFixed(2)}</p>
                                <p className="text-[11px] text-[#94A3B8]">{inCart ? `En carrito (${inCart.quantity})` : (noStock ? 'Solicitar traspaso →' : 'Agregar +')}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {newSale.items.map((it, idx) => {
                        const prod = products.find(p => p.product_id === it.product_id);
                        const isService = prod?.item_type === 'servicio';
                        const insufficient = prod && !isService && (it.quantity || 0) > (prod.stock || 0);
                        return (
                          <div key={idx} className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-2 py-2" data-testid={`item-row-${idx}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#022C22] truncate">{it.name || prod?.name || 'Producto'}</p>
                              <p className={`text-[11px] ${insufficient ? 'text-red-600 font-semibold' : 'text-[#64748B]'}`} data-testid={`stock-hint-${idx}`}>
                                {isService ? 'Servicio' : insufficient ? `⚠ Stock insuficiente: disponible ${prod?.stock || 0}` : `Disponible: ${prod?.stock ?? '-'}`}
                              </p>
                              <ImeiPicker productId={it.product_id} value={it.imeis || []} qty={it.quantity || 1} onChange={(imeis) => updateItem(idx, { imeis })} />
                            </div>
                            <div className="flex items-center border border-[#E2E8F0] rounded-md overflow-hidden bg-white">
                              <button type="button" onClick={() => updateItem(idx, { quantity: Math.max(1, (it.quantity || 1) - 1) })} className="px-2 py-1 text-[#64748B] hover:bg-[#F1F5F9]" data-testid={`qty-minus-${idx}`}>−</button>
                              <input type="number" min="1" value={it.quantity}
                                onChange={(e) => updateItem(idx, { quantity: parseInt(e.target.value) || 0 })}
                                className="w-12 text-center text-sm border-x border-[#E2E8F0] py-1 focus:outline-none" data-testid={`quantity-input-${idx}`} />
                              <button type="button" onClick={() => updateItem(idx, { quantity: (it.quantity || 0) + 1 })} className="px-2 py-1 text-[#64748B] hover:bg-[#F1F5F9]" data-testid={`qty-plus-${idx}`}>+</button>
                            </div>
                            <div className="w-24">
                              <Input type="number" step="0.01" value={it.price}
                                onChange={(e) => updateItem(idx, { price: parseFloat(e.target.value) || 0 })} className="text-sm" />
                            </div>
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        );
                      })}
                      {newSale.items.length === 0 && (
                        <p className="text-sm text-center text-[#94A3B8] py-3">Busca arriba y toca un producto para agregarlo</p>
                      )}
                    </div>
                    {newSale.items.length > 0 && (
                      <div className="mt-2 pt-2 border-t space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-[#64748B]">Subtotal:</span><span>${calcSubtotal().toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-[#64748B]">IVA ({newSale.tax_rate || 0}%):</span><span>${calcTax().toFixed(2)}</span></div>
                        <div className="flex justify-between font-bold text-base"><span>Total:</span><span>${calcTotal().toFixed(2)}</span></div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tipo de documento</Label>
                      <select
                        value={newSale.document_type}
                        onChange={(e) => setNewSale({ ...newSale, document_type: e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                        data-testid="sale-document-type"
                      >
                        <option value="factura">Factura</option>
                        {vFlags.enable_credit_plans !== false && <option value="nota_entrega">Nota de entrega</option>}
                        <option value="presupuesto">Presupuesto</option>
                      </select>
                    </div>
                    <div>
                      <Label>Tipo de operación</Label>
                      <select
                        value={newSale.document_type === 'nota_entrega' ? 'credito' : newSale.operation_type}
                        onChange={(e) => setNewSale({ ...newSale, operation_type: e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white disabled:bg-gray-100"
                        data-testid="sale-operation-type"
                        disabled={newSale.document_type === 'presupuesto' || newSale.document_type === 'nota_entrega'}
                      >
                        <option value="contado">Contado (pago 100%)</option>
                        {vFlags.enable_credit_plans !== false && <option value="credito">Crédito (requiere aprobación)</option>}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label>IVA (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newSale.tax_rate}
                        onChange={(e) => setNewSale({ ...newSale, tax_rate: e.target.value })}
                        data-testid="sale-tax-rate"
                      />
                    </div>
                  </div>
                  {newSale.document_type !== 'presupuesto' && (newSale.document_type === 'nota_entrega' || newSale.operation_type === 'credito') && (
                    <FinancingPlan sale={newSale} setSale={setNewSale} total={calcTotal()} />
                  )}
                  {newSale.document_type === 'presupuesto' && (
                    <p className="text-xs text-[#64748B]">El presupuesto no afecta inventario ni contabilidad hasta convertirse en factura.</p>
                  )}
                  {(newSale.document_type === 'nota_entrega' || newSale.operation_type === 'credito') && newSale.document_type !== 'presupuesto' && (
                    <p className="text-xs text-orange-600">Operación a crédito: requiere que el cliente tenga línea de crédito aprobada por el Master.</p>
                  )}
                  <div>
                    <Label>Notas</Label>
                    <Input value={newSale.notes} onChange={(e) => setNewSale({ ...newSale, notes: e.target.value })} />
                  </div>
                  <Button type="button" variant="outline" className="w-full" disabled={!newSale.items.length} onClick={() => setPreview(true)} data-testid="preview-sale-button"><Eye size={15} className="mr-1" /> Previsualizar documento</Button>
                  <Button type="submit" className="w-full" data-testid="submit-sale-button">Crear venta</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Conteo facturación (solo master) */}
      {isMaster && (
        <Card className="border border-[#E2E8F0] bg-gradient-to-r from-[#004D40] to-[#00796B] text-white">
          <CardContent className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-white/70 uppercase">Total facturado</p>
              <p className="text-2xl sm:text-3xl font-bold">${totalAmount.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-white/70 uppercase">Facturas</p>
              <p className="text-2xl sm:text-3xl font-bold">{totalCount}</p>
            </div>
            <div>
              <p className="text-xs text-white/70 uppercase">Notas de entrega</p>
              <p className="text-2xl sm:text-3xl font-bold">{filteredSales.filter(s => ['pendiente_despacho','en_transito','despachado'].includes(s.status)).length}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-3 flex flex-wrap gap-2">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" data-testid="filter-status">
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterDoc} onChange={(e) => setFilterDoc(e.target.value)}
            className="px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" data-testid="filter-doc-type">
            <option value="">Todos los documentos</option>
            <option value="factura">Facturas</option>
            <option value="nota_entrega">Notas de entrega</option>
            <option value="presupuesto">Presupuestos</option>
          </select>
          {isMaster && (
            <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}
              className="px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" data-testid="filter-company">
              <option value="">Todas las empresas</option>
              {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
          )}
        </CardContent>
      </Card>

      {/* Lista de ventas */}
      <div className="space-y-2 sm:space-y-3">
        {filteredSales.map((sale) => {
          const info = statusInfo(sale.status);
          return (
            <Card key={sale.sale_id} className="border border-[#E2E8F0] hover:shadow-md transition-shadow cursor-pointer" data-testid={`sale-card-${sale.sale_id}`} onClick={() => setDetailSale(sale)}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-bold">{sale.invoice_number || `#${sale.sale_id.substring(0, 8)}`}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>{info.label}</span>
                    </div>
                    <p className="text-sm text-[#022C22] font-medium truncate">{sale.client_name || clientName(sale.client_id)}</p>
                    <p className="text-xs text-[#64748B]">{sale.items?.length || 0} ítem(s) · {new Date(sale.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3">
                    <p className="font-bold text-lg sm:text-xl">${sale.total.toFixed(2)}</p>
                    <Button size="icon" variant="outline" onClick={(e) => { e.stopPropagation(); setDetailSale(sale); }} data-testid={`view-sale-${sale.sale_id}`}>
                      <Eye size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filteredSales.length === 0 && (
          <p className="text-center text-[#94A3B8] py-12">No hay ventas que coincidan con los filtros</p>
        )}
      </div>

      {/* Dialog Detalle Venta */}
      {detailSale && (
        <Dialog open={!!detailSale} onOpenChange={(o) => { if (!o) { setDetailSale(null); setSalePayments([]); } else { loadSalePayments(detailSale.sale_id); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail-dialog">
            <DialogHeader>
              <DialogTitle>Venta {detailSale.invoice_number || detailSale.sale_id.substring(0, 8)}</DialogTitle>
            </DialogHeader>
            <DocShare kind="sale" refId={detailSale.sale_id} label={`Recibo ${detailSale.invoice_number || ''}`} />
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <p><b>Cliente:</b> {detailSale.client_name || clientName(detailSale.client_id)}</p>
                <p><b>Tipo:</b> {detailSale.document_type === 'nota_entrega' ? 'Nota de entrega' : detailSale.document_type === 'presupuesto' ? 'Presupuesto' : 'Factura'}</p>
                <p><b>Subtotal:</b> ${(detailSale.subtotal ?? detailSale.total).toFixed(2)}</p>
                <p><b>IVA ({detailSale.tax_rate || 0}%):</b> ${(detailSale.tax_amount || 0).toFixed(2)}</p>
                <p><b>Total:</b> ${detailSale.total.toFixed(2)}</p>
                <p><b>Pago:</b> {detailSale.payment_status} (${(detailSale.amount_paid||0).toFixed(2)}/${detailSale.total.toFixed(2)})</p>
                <p><b>Estado:</b> <span className={`px-2 py-0.5 rounded text-xs ${statusInfo(detailSale.status).color}`}>{statusInfo(detailSale.status).label}</span></p>
              </div>
              <div>
                <p className="font-semibold mb-1">Items:</p>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F8F9FA]">
                      <tr><th className="p-2 text-left">Producto</th><th className="p-2">Cant</th><th className="p-2 text-right">Precio</th><th className="p-2 text-right">Sub</th></tr>
                    </thead>
                    <tbody>
                      {detailSale.items?.map((it, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{it.name || productName(it.product_id)}{it.over_stock && <span className="ml-1 text-orange-600">⚠️</span>}</td>
                          <td className="p-2 text-center">{it.quantity}</td>
                          <td className="p-2 text-right">${it.price?.toFixed(2)}</td>
                          <td className="p-2 text-right font-medium">${((it.price||0) * (it.quantity||0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Historial de pagos */}
              {salePayments.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Pagos recibidos:</p>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F8F9FA]">
                        <tr><th className="p-2 text-left">Fecha</th><th className="p-2 text-left">Método</th><th className="p-2 text-left">Ref.</th><th className="p-2 text-right">Monto</th></tr>
                      </thead>
                      <tbody>
                        {salePayments.map(p => (
                          <tr key={p.payment_id} className="border-t" data-testid={`payment-row-${p.payment_id}`}>
                            <td className="p-2">{new Date(p.created_at).toLocaleString()}</td>
                            <td className="p-2">{p.method_name || '-'}</td>
                            <td className="p-2 font-mono text-[10px]">{p.reference || '-'}</td>
                            <td className="p-2 text-right font-bold">${p.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Conversión de presupuesto */}
              {detailSale.document_type === 'presupuesto' && ['master','superadmin','admin','vendedor'].includes(user?.role_id) && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3" data-testid="convert-budget-section">
                  <p className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                    <ArrowRightLeft size={13} /> Este es un presupuesto. Conviértelo en documento fiscal:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => convertBudget(detailSale, 'factura')} data-testid="convert-to-factura">
                      Convertir a Factura
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => convertBudget(detailSale, 'nota_entrega')} data-testid="convert-to-nota">
                      Convertir a Nota de entrega
                    </Button>
                  </div>
                </div>
              )}

              {/* Cambio de estado */}
              <div className="flex flex-wrap gap-2 pt-3 border-t">
                {canProcess && detailSale.status === 'abierto' && (
                  <Button size="sm" onClick={() => changeStatus(detailSale, 'procesada')} data-testid="btn-process">
                    <CheckCircle2 size={14} className="mr-1" /> Procesar
                  </Button>
                )}
                {canProcess && detailSale.status === 'pendiente_procesar' && (
                  <Button size="sm" onClick={() => changeStatus(detailSale, 'procesada')}>
                    <CheckCircle2 size={14} className="mr-1" /> Confirmar y procesar
                  </Button>
                )}
                {canProcess && detailSale.status === 'procesada' && (
                  <Button size="sm" onClick={() => changeStatus(detailSale, 'pendiente_despacho')}>
                    <Truck size={14} className="mr-1" /> Pasar a despacho
                  </Button>
                )}
                {['admin','master','superadmin','despacho'].includes(user?.role_id) && detailSale.status === 'pendiente_despacho' && (
                  <Button size="sm" onClick={() => changeStatus(detailSale, 'en_transito')}>
                    <Truck size={14} className="mr-1" /> En tránsito
                  </Button>
                )}
                {['admin','master','superadmin','despacho'].includes(user?.role_id) && detailSale.status === 'en_transito' && (
                  <Button size="sm" onClick={() => changeStatus(detailSale, 'despachado')}>
                    <CheckCircle2 size={14} className="mr-1" /> Entregado
                  </Button>
                )}
              </div>

              {/* Documentos (descargar/imprimir/compartir/whatsapp) + Pago */}
              <div className="space-y-3 pt-3 border-t">
                {canProcess && detailSale.payment_status !== 'pagado' && (
                  <Button size="sm" onClick={() => openPaymentDialog(detailSale)} data-testid="btn-register-payment">
                    <CreditCard size={14} className="mr-1" /> Registrar pago
                  </Button>
                )}
                <div>
                  <p className="text-xs font-semibold text-[#64748B] mb-1.5 flex items-center gap-1">
                    <FileText size={12} /> {detailSale.document_type === 'nota_entrega' ? 'Nota de entrega' : 'Factura'} {detailSale.invoice_number}
                  </p>
                  <DocumentActions
                    fetchBlob={() => api.getSaleTicket(detailSale.sale_id)}
                    filename={`${detailSale.invoice_number || 'documento'}.pdf`}
                    title={`SalesPRO — ${detailSale.document_type === 'nota_entrega' ? 'Nota de entrega' : 'Factura'} ${detailSale.invoice_number} · ${clientName(detailSale.client_id)} · Total $${detailSale.total.toFixed(2)}`}
                    phone={clients.find(c => c.client_id === detailSale.client_id)?.phone || ''}
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#64748B] mb-1.5 flex items-center gap-1">
                    <Truck size={12} /> Guía de despacho (sin precios)
                  </p>
                  <DocumentActions
                    fetchBlob={() => api.getDispatchTicket(detailSale.sale_id)}
                    filename={`guia_${detailSale.invoice_number || 'despacho'}.pdf`}
                    title={`SalesPRO — Guía de despacho ${detailSale.invoice_number} · ${clientName(detailSale.client_id)}`}
                    phone={clients.find(c => c.client_id === detailSale.client_id)?.phone || ''}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Pago */}
      {paymentOpen && (
        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="max-w-md" data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle>Registrar pago</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Monto *</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="payment-amount" autoFocus />
              </div>
              <div>
                <Label>Método</Label>
                <select
                  value={paymentForm.method_id}
                  onChange={(e) => {
                    const m = paymentMethods.find(pm => pm.method_id === e.target.value);
                    setPaymentForm({ ...paymentForm, method_id: e.target.value, method_name: m?.name || '' });
                  }}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                  data-testid="payment-method-select"
                >
                  {paymentMethods.map(m => <option key={m.method_id} value={m.method_id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Referencia (opcional)</Label>
                <Input value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="Nº comprobante, hash crypto..." />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
              </div>
              <Button onClick={submitPayment} className="w-full" data-testid="submit-payment">Confirmar pago</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!xfer} onOpenChange={(o) => !o && setXfer(null)}>
        <DialogContent className="max-w-md" data-testid="xfer-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft size={18} /> Solicitar traspaso</DialogTitle></DialogHeader>
          {xfer && (
            <div className="space-y-3">
              <p className="text-sm text-[#475569]">No hay stock de <b>{xfer.product?.name}</b> en esta sucursal. Estas tiendas tienen disponibilidad; solicita el traspaso y la entrega quedará <b>EN ESPERA</b> hasta que la otra sucursal apruebe.</p>
              <div className="space-y-2">
                {xfer.options.map((opt) => (
                  <div key={opt.company_id} className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2" data-testid={`xfer-opt-${opt.company_id}`}>
                    <div>
                      <p className="text-sm font-medium text-[#022C22]">{opt.company_name}</p>
                      <p className="text-[11px] text-[#64748B]">Disponibles: {opt.stock}</p>
                    </div>
                    <Button size="sm" onClick={() => requestXfer(opt)} data-testid={`xfer-request-${opt.company_id}`}>Solicitar</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye size={18} /> Previsualización del documento</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="border-b border-[#E2E8F0] pb-2">
              <p className="font-bold text-[#022C22] text-base">{(companies.find((c) => c.company_id === newSale.company_id)?.brand_name) || (companies.find((c) => c.company_id === newSale.company_id)?.name) || 'SalesPRO'}</p>
              <p className="text-[#64748B] text-xs">{newSale.document_type} · Lista de precio {newSale.price_list} · {newSale.operation_type}</p>
              <p className="text-[#64748B] text-xs">Cliente: {clients.find((c) => c.client_id === newSale.client_id)?.name || '—'}</p>
            </div>
            <table className="w-full text-xs" data-testid="preview-items">
              <thead><tr className="text-left text-[#64748B]"><th className="py-1">Artículo</th><th className="text-center">Cant.</th><th className="text-right">Precio</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {newSale.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-[#F1F5F9]">
                    <td className="py-1">{it.name}</td>
                    <td className="text-center">{it.quantity}</td>
                    <td className="text-right">
                      {isSuper
                        ? <Input type="number" value={it.price} onChange={(e) => setItemPrice(idx, e.target.value)} className="h-7 w-24 ml-auto text-right" data-testid={`preview-price-${idx}`} />
                        : `$${(parseFloat(it.price) || 0).toFixed(2)}`}
                    </td>
                    <td className="text-right">${((parseFloat(it.price) || 0) * (parseInt(it.quantity, 10) || 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="space-y-1 pt-2 border-t border-[#E2E8F0]">
              <div className="flex justify-between"><span className="text-[#64748B]">Subtotal:</span><span>${calcSubtotal().toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">IVA ({newSale.tax_rate}%):</span><span>${calcTax().toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-base"><span>Total:</span><span>${calcTotal().toFixed(2)}</span></div>
            </div>
            {isSuper && <p className="text-[11px] text-[#94A3B8]">Como usuario superior puedes ajustar los precios aquí antes de generar.</p>}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setPreview(false)} data-testid="preview-close">Seguir editando</Button>
              <Button className="flex-1" onClick={() => { setPreview(false); handleCreate({ preventDefault: () => {} }); }} data-testid="preview-confirm">Confirmar y generar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
